<script setup>
import { useI18n } from 'vue-i18n';
import Drawer from '../../website/components/Drawer.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  bookmark: {
    type: Object,
    default: null,
  },
  show: {
    type: Boolean,
    required: true,
  },
});

const emit = defineEmits(['close', 'edit', 'delete']);

const { t } = useI18n();

const handleClose = () => {
  emit('close');
};

const handleEdit = () => {
  emit('edit', props.bookmark);
  handleClose();
};

const handleDelete = () => {
  emit('delete', props.bookmark);
  handleClose();
};

const handleOpenUrl = () => {
  if (props.bookmark?.url) {
    window.open(props.bookmark.url, '_blank', 'noopener,noreferrer');
  }
};

const formatDate = dateString => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleDateString('fr-FR', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};
</script>

<template>
  <Drawer :show="show" size="md" @close="handleClose">
    <!-- Header -->
    <div
      class="flex items-center justify-between px-6 py-4 border-b border-n-weak"
    >
      <div class="flex items-center gap-3 min-w-0 flex-1">
        <div
          class="flex-shrink-0 w-10 h-10 rounded-lg bg-n-blue-2 dark:bg-n-blue-3 flex items-center justify-center"
        >
          <span class="i-lucide-bookmark size-5 text-n-blue-10" />
        </div>
        <h2
          class="text-lg font-semibold text-n-slate-12 truncate"
          :title="bookmark?.title"
        >
          {{ bookmark?.title || '' }}
        </h2>
      </div>
      <button
        class="flex items-center justify-center w-8 h-8 rounded-lg hover:bg-n-alpha-1 text-n-slate-10 hover:text-n-slate-12 transition-colors flex-shrink-0"
        @click="handleClose"
      >
        <span class="i-lucide-x size-5" />
      </button>
    </div>

    <!-- Content -->
    <div class="flex-1 overflow-y-auto custom-scrollbar">
      <!-- Thumbnail encart -->
      <div class="px-6 pt-6">
        <div
          class="relative w-full max-w-sm mx-auto rounded-2xl overflow-hidden border border-n-weak bg-n-alpha-2 group shadow-sm hover:shadow-md transition-all duration-500"
          :class="
            bookmark?.thumbnail_url
              ? 'aspect-video'
              : 'aspect-video flex items-center justify-center'
          "
        >
          <img
            v-if="bookmark?.thumbnail_url"
            :src="bookmark.thumbnail_url"
            :alt="bookmark.title"
            class="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
            loading="lazy"
          />
          <div
            v-else
            class="w-full h-full flex flex-col items-center justify-center gap-4 bg-gradient-to-br from-n-alpha-2 to-n-alpha-3"
          >
            <div
              class="w-16 h-16 rounded-2xl bg-n-blue-2 dark:bg-n-blue-3 flex items-center justify-center shadow-inner"
            >
              <span
                class="i-lucide-globe size-8 text-n-blue-10 animate-pulse"
              />
            </div>
            <p
              class="text-[11px] font-black uppercase tracking-[0.2em] text-n-slate-10"
            >
              {{ t('BOOKMARKS.DRAWER.NO_THUMBNAIL') }}
            </p>
          </div>
          <!-- Overlay au survol -->
          <div
            v-if="bookmark?.thumbnail_url"
            class="absolute inset-0 bg-n-black/20 opacity-0 group-hover:opacity-100 transition-opacity duration-500 flex items-center justify-center backdrop-blur-[2px]"
          >
            <button
              class="p-4 rounded-2xl bg-white/90 dark:bg-n-black/90 shadow-2xl hover:scale-110 transition-all duration-300"
              @click="handleOpenUrl"
            >
              <span class="i-lucide-external-link size-6 text-n-brand" />
            </button>
          </div>
        </div>
      </div>

      <!-- Main content -->
      <div class="px-6 py-8 space-y-8">
        <!-- URL -->
        <div class="space-y-3">
          <div class="flex items-center justify-between">
            <label
              class="text-[10px] font-black uppercase tracking-[0.2em] text-n-slate-10 ml-1"
            >
              {{ t('BOOKMARKS.DRAWER.DIRECT_LINK') }}
            </label>
            <span class="h-px flex-1 bg-n-weak ml-4" />
          </div>
          <div
            class="flex items-center gap-3 p-4 rounded-2xl bg-n-alpha-1 border border-n-weak hover:border-n-brand/30 transition-colors group/url shadow-sm"
          >
            <div
              class="w-10 h-10 rounded-xl bg-n-brand/10 flex items-center justify-center shrink-0"
            >
              <span class="i-lucide-link size-5 text-n-brand" />
            </div>
            <a
              :href="bookmark?.url"
              target="_blank"
              rel="noopener noreferrer"
              class="text-sm font-bold text-n-slate-12 hover:text-n-brand truncate flex-1 min-w-0 transition-colors"
              @click.stop
            >
              {{ bookmark?.url }}
            </a>
            <button
              class="p-2 rounded-lg hover:bg-n-brand/10 text-n-slate-10 hover:text-n-brand transition-all flex-shrink-0"
              @click="handleOpenUrl"
            >
              <span class="i-lucide-external-link size-5" />
            </button>
          </div>
        </div>

        <!-- Description -->
        <div v-if="bookmark?.description" class="space-y-3">
          <div class="flex items-center justify-between">
            <label
              class="text-[10px] font-black uppercase tracking-[0.2em] text-n-slate-10 ml-1"
            >
              {{ t('BOOKMARKS.FORM.DESCRIPTION') }}
            </label>
            <span class="h-px flex-1 bg-n-weak ml-4" />
          </div>
          <div
            class="p-5 rounded-2xl bg-n-alpha-1 border border-n-weak shadow-sm"
          >
            <p
              class="text-sm font-medium text-n-slate-11 leading-relaxed whitespace-pre-wrap italic"
            >
              &quot;{{ bookmark.description }}&quot;
            </p>
          </div>
        </div>

        <!-- Keywords -->
        <div
          v-if="bookmark?.keywords_array && bookmark.keywords_array.length > 0"
          class="space-y-3"
        >
          <div class="flex items-center justify-between">
            <label
              class="text-[10px] font-black uppercase tracking-[0.2em] text-n-slate-10 ml-1"
            >
              {{ t('BOOKMARKS.FORM.KEYWORDS') }}
            </label>
            <span class="h-px flex-1 bg-n-weak ml-4" />
          </div>
          <div class="flex flex-wrap gap-2.5">
            <span
              v-for="keyword in bookmark.keywords_array"
              :key="keyword"
              class="px-3.5 py-1.5 text-[11px] font-black uppercase tracking-wider bg-n-brand/10 text-n-brand rounded-full border border-n-brand/20 shadow-brand-sm"
            >
              #{{ keyword }}
            </span>
          </div>
        </div>

        <!-- Dates -->
        <div class="grid grid-cols-2 gap-6 pt-6">
          <div
            v-if="bookmark?.created_at"
            class="p-4 rounded-2xl bg-n-alpha-1 border border-n-weak shadow-sm"
          >
            <label
              class="block text-[10px] font-black uppercase tracking-widest text-n-slate-10 mb-2"
            >
              {{ t('BOOKMARKS.DRAWER.CREATED_AT') }}
            </label>
            <p class="text-xs font-bold text-n-slate-12">
              {{ formatDate(bookmark.created_at) }}
            </p>
          </div>
          <div
            v-if="bookmark?.updated_at"
            class="p-4 rounded-2xl bg-n-alpha-1 border border-n-weak shadow-sm"
          >
            <label
              class="block text-[10px] font-black uppercase tracking-widest text-n-slate-10 mb-2"
            >
              {{ t('BOOKMARKS.DRAWER.UPDATED_AT') }}
            </label>
            <p class="text-xs font-bold text-n-slate-12">
              {{ formatDate(bookmark.updated_at) }}
            </p>
          </div>
        </div>
      </div>
    </div>

    <!-- Footer Actions -->
    <div
      class="flex items-center justify-between px-6 py-4 border-t border-n-weak bg-n-alpha-1"
    >
      <Button
        icon="i-lucide-trash-2"
        color="ruby"
        size="sm"
        variant="ghost"
        :label="t('BOOKMARKS.DELETE')"
        @click="handleDelete"
      />
      <div class="flex items-center gap-2">
        <Button
          icon="i-lucide-external-link"
          color="slate"
          size="sm"
          variant="ghost"
          :label="t('BOOKMARKS.OPEN')"
          @click="handleOpenUrl"
        />
        <Button
          icon="i-lucide-pencil"
          color="slate"
          size="sm"
          :label="t('BOOKMARKS.EDIT')"
          @click="handleEdit"
        />
      </div>
    </div>
  </Drawer>
</template>
